import React, { useRef, useCallback, useMemo } from "react";
import ForceGraph2D from "react-force-graph-2d";

/**
 * Interactive social network graph visualization
 *
 * @param {Object} props
 * @param {Array} props.nodes - Array of node objects with id, username, opinionPosition, artificial, etc.
 * @param {Array} props.edges - Array of edge objects with source and target IDs
 * @param {string} props.colorBy - What to color nodes by: "opinion", "type", "orientation"
 * @param {string} props.sizeBy - What to size nodes by: "followers", "activity", "equal"
 * @param {Function} props.onNodeClick - Callback when a node is clicked
 */
const NetworkGraph = ({
  nodes = [],
  edges = [],
  colorBy = "opinion",
  sizeBy = "followers",
  onNodeClick,
  width = 600,
  height = 400,
}) => {
  const graphRef = useRef();

  // Convert data to format expected by force-graph
  const graphData = useMemo(() => {
    return {
      nodes: nodes.map(node => ({
        ...node,
        id: node.id,
      })),
      links: edges.map(edge => ({
        source: edge.source,
        target: edge.target,
      })),
    };
  }, [nodes, edges]);

  // Color scale for opinion position (-1 to +1)
  const getOpinionColor = useCallback((position) => {
    // Red (contra) -> Gray (neutral) -> Blue (pro)
    if (position < -0.3) {
      // Contra: Red shades
      const intensity = Math.min(1, Math.abs(position));
      return `rgb(${180 + 75 * intensity}, ${80 - 40 * intensity}, ${80 - 40 * intensity})`;
    } else if (position > 0.3) {
      // Pro: Blue shades
      const intensity = Math.min(1, position);
      return `rgb(${80 - 40 * intensity}, ${120 + 60 * intensity}, ${200 + 55 * intensity})`;
    } else {
      // Neutral: Gray
      return "rgb(150, 150, 150)";
    }
  }, []);

  // Get node color based on colorBy prop
  const getNodeColor = useCallback((node) => {
    switch (colorBy) {
      case "opinion":
        return getOpinionColor(node.opinionPosition || 0);
      case "type":
        return node.artificial ? "#9333ea" : "#22c55e"; // Purple for AI, Green for human
      case "orientation":
        switch (node.groupOrientation) {
          case "ingroup": return "#3b82f6"; // Blue
          case "outgroup": return "#ef4444"; // Red
          default: return "#a3a3a3"; // Gray for neutral
        }
      default:
        return "#6b7280";
    }
  }, [colorBy, getOpinionColor]);

  // Get node size based on sizeBy prop
  const getNodeSize = useCallback((node) => {
    const baseSize = 6;
    switch (sizeBy) {
      case "followers":
        return baseSize + Math.sqrt(node.followerCount || 0) * 2;
      case "activity":
        return baseSize + Math.sqrt((node.messages || 0) + (node.comments || 0)) * 2;
      case "equal":
      default:
        return baseSize;
    }
  }, [sizeBy]);

  // Custom node rendering
  const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const size = getNodeSize(node);
    const color = getNodeColor(node);
    const fontSize = 10 / globalScale;
    const label = node.username;

    // Draw node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    // Draw border (thicker for humans)
    ctx.strokeStyle = node.artificial ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.8)";
    ctx.lineWidth = node.artificial ? 1 : 2;
    ctx.stroke();

    // Draw label if zoomed in enough
    if (globalScale > 0.8) {
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.fillText(label, node.x, node.y + size + fontSize);
    }
  }, [getNodeColor, getNodeSize]);

  // Handle node click
  const handleNodeClick = useCallback((node) => {
    if (onNodeClick) {
      onNodeClick(node);
    }
    // Center on node
    if (graphRef.current) {
      graphRef.current.centerAt(node.x, node.y, 500);
      graphRef.current.zoom(2, 500);
    }
  }, [onNodeClick]);

  // Handle background click to reset zoom
  const handleBackgroundClick = useCallback(() => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400, 50);
    }
  }, []);

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-800 rounded-lg">
        <p className="text-gray-400">No network data available</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-lg overflow-hidden">
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        width={width}
        height={height}
        nodeCanvasObject={nodeCanvasObject}
        nodePointerAreaPaint={(node, color, ctx) => {
          const size = getNodeSize(node);
          ctx.beginPath();
          ctx.arc(node.x, node.y, size + 2, 0, 2 * Math.PI);
          ctx.fillStyle = color;
          ctx.fill();
        }}
        linkColor={() => "rgba(255, 255, 255, 0.2)"}
        linkWidth={1}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={0.9}
        linkCurvature={0.1}
        onNodeClick={handleNodeClick}
        onBackgroundClick={handleBackgroundClick}
        backgroundColor="transparent"
        cooldownTicks={100}
        onEngineStop={() => {
          if (graphRef.current) {
            graphRef.current.zoomToFit(400, 50);
          }
        }}
      />
    </div>
  );
};

export default NetworkGraph;
